/**
 * Stinner — split combo selector (high-variant, N-field).
 *
 * Gated in Liquid: snippets/pdp-split-combo-selector.liquid
 *
 * HIGH-VARIANT SAFE — does NOT depend on product.variants.
 * Uses product.options_with_values (no 250-variant cap) for initial render
 * and the Section Rendering API (sections/stinner-split-combo-data.liquid)
 * for availability refreshes on every selection change.
 *
 * How it works:
 *   1. Liquid emits comboValues (from product_option_value objects) + nativeOptionGroups.
 *   2. JS parses each combo value string by delimiter to build N button groups.
 *   3. On any selection change, JS fetches the data section with ?option_values=id1,id2,id3.
 *   4. Response JSON contains updated availability per combo value + the resolved variant ID.
 *   5. JS refreshes button-group disabled states, syncs the master <select>, and dispatches
 *      variantChange so Impulse updates price / buy-button state.
 */
(function () {
  'use strict';

  var WARN_PREFIX = '[Stinner split-combo]';
  var DATA_SECTION_ID = 'stinner-split-combo-data';

  // Candidate delimiters tried during auto-detection (order matters — try longer first).
  var DELIMITER_CANDIDATES = [
    ' / ',
    ' | ',
    ' + ',
    ' & ',
    ' – ',
    ' - ',
    '/',
    '|',
    '&',
    '+',
  ];

  // ── Utility ─────────────────────────────────────────────────────────────────

  function norm(s) {
    if (s == null) return '';
    return String(s).trim().toLowerCase();
  }

  function uniqueByNorm(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var k = norm(arr[i]);
      if (k !== '' && !seen[k]) {
        seen[k] = true;
        out.push(arr[i]);
      }
    }
    return out;
  }

  function otherOptionKeys(comboIdx) {
    return ['option1', 'option2', 'option3'].filter(function (_k, i) {
      return i !== comboIdx;
    });
  }

  function comboOptionKey(comboIdx) {
    return 'option' + (comboIdx + 1);
  }

  // ── DOM helpers ──────────────────────────────────────────────────────────────

  function getOptionValue(section, optionKey) {
    var sel = section.querySelector('select[data-variant-input][data-index="' + optionKey + '"]');
    if (sel) return sel.value;
    var checked = section.querySelector(
      'input[type="radio"][data-variant-input][data-index="' + optionKey + '"]:checked'
    );
    return checked ? checked.value : null;
  }

  function setOptionValue(section, optionKey, value) {
    var sel = section.querySelector('select[data-variant-input][data-index="' + optionKey + '"]');
    if (sel) {
      sel.value = value;
      return sel;
    }
    var radios = section.querySelectorAll(
      'input[type="radio"][data-variant-input][data-index="' + optionKey + '"]'
    );
    var trigger = null;
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      r.checked = r.value === value;
      if (r.value === value) trigger = r;
    }
    return trigger;
  }

  function findNativeComboWrapper(section, comboKey) {
    var inner = section.querySelector('[data-index="' + comboKey + '"]');
    return inner ? inner.closest('.variant-wrapper') : null;
  }

  function getRadioGroupValue(fieldset) {
    var r = fieldset.querySelector('input[type="radio"]:checked');
    return r ? r.value : '';
  }

  /**
   * Impulse theme.Variants._onSelectChange destructures { srcElement } from the event.
   * Synthetic Event() objects often omit srcElement; without it the handler throws.
   */
  function dispatchVariantInputChange(el) {
    if (!el) return;
    var ev = new Event('change', { bubbles: true });
    try {
      Object.defineProperty(ev, 'srcElement', { value: el, enumerable: true });
    } catch (e) {}
    el.dispatchEvent(ev);
  }

  /**
   * Ensure the master <select name="id"> has an <option> for this variant ID.
   * Variants 251+ are not emitted by the Liquid loop so we add them dynamically.
   */
  function ensureMasterSelectOption(master, variantId) {
    if (!master) return;
    var vid = String(variantId);
    if (!master.querySelector('option[value="' + vid + '"]')) {
      var opt = document.createElement('option');
      opt.value = vid;
      master.appendChild(opt);
    }
  }

  /** Keep master <select name="id"> in sync — required for /cart/add.js form POST. */
  function syncMasterVariantSelect(section, variantId) {
    var master = section.querySelector('[data-product-select]');
    if (!master || !variantId) return;
    var vid = String(variantId);
    ensureMasterSelectOption(master, vid);
    master.value = vid;
    master.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mirror Impulse Variants._getCurrentOptions so VariantAvailability receives
   * current Color / Size / Combo selections (theme.js singleOptionSelector).
   */
  function buildCurrentlySelectedValues(section) {
    var result = [];
    section.querySelectorAll('[data-variant-input]').forEach(function (el) {
      var type = el.getAttribute('type');
      if (type === 'radio' || type === 'checkbox') {
        if (el.checked) {
          result.push({
            value: el.value,
            index: el.getAttribute('data-index'),
          });
        }
      } else {
        result.push({
          value: el.value,
          index: el.getAttribute('data-index'),
        });
      }
    });
    if (typeof theme !== 'undefined' && theme.utils && typeof theme.utils.compact === 'function') {
      return theme.utils.compact(result);
    }
    return result.filter(function (row) {
      return row && row.index != null && row.index !== '' && row.value != null && row.value !== '';
    });
  }

  function dispatchUnavailable(section) {
    var detail = {
      variant: null,
      currentlySelectedValues: buildCurrentlySelectedValues(section),
      value: null,
      index: null,
    };
    section.dispatchEvent(new CustomEvent('variantChange', { detail: detail, bubbles: true }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail: detail }));
  }

  function flexCountForValues(len) {
    if (len <= 0) return 1;
    return len > 3 ? 4 : len;
  }

  /** Impulse-style button radio group (same pattern as variant-button.liquid). */
  function fillButtonGroup(fieldset, values, preferred, opts) {
    var productId = opts.productId;
    var radioName = opts.radioName;
    var legendText = opts.legendText;
    var soldOutValues = opts.soldOutValues || {};
    var fc = flexCountForValues(values.length);
    fieldset.className = 'variant-input-wrap flex-count-' + fc;
    fieldset.innerHTML = '';
    var leg = document.createElement('legend');
    leg.className = 'hide';
    leg.textContent = legendText;
    fieldset.appendChild(leg);
    var prevNorm = preferred != null && preferred !== '' ? norm(preferred) : '';
    var checkedAny = false;
    for (var i = 0; i < values.length; i++) {
      var val = values[i];
      var vid = 'stinner-' + radioName + '-' + productId + '-' + i;
      var wrap = document.createElement('div');
      wrap.className = 'variant-input variant-count-' + fc;
      var inp = document.createElement('input');
      inp.type = 'radio';
      inp.name = 'stinner-split-' + radioName + '-' + productId;
      inp.value = val;
      inp.id = vid;
      if (prevNorm && norm(val) === prevNorm) {
        inp.checked = true;
        checkedAny = true;
      }
      var lab = document.createElement('label');
      lab.htmlFor = vid;
      lab.className = 'variant__button-label variant-width-' + fc;
      lab.textContent = val;
      if (soldOutValues[norm(val)]) {
        inp.classList.add('disabled');
        lab.classList.add('disabled');
      }
      wrap.appendChild(inp);
      wrap.appendChild(lab);
      fieldset.appendChild(wrap);
    }
    if (!checkedAny && values.length) {
      var first = fieldset.querySelector('input[type="radio"]');
      if (first) first.checked = true;
    }
  }

  // ── Delimiter handling ───────────────────────────────────────────────────────

  function splitComboValue(value, delimiter) {
    return value.split(delimiter).map(function (p) { return p.trim(); });
  }

  function delimiterValidForAll(comboValues, delimiter, fieldCount) {
    for (var i = 0; i < comboValues.length; i++) {
      if (splitComboValue(comboValues[i].value, delimiter).length !== fieldCount) {
        return false;
      }
    }
    return true;
  }

  /**
   * Auto-detect delimiter: first candidate for which every combo value splits
   * into exactly `fieldCount` parts (avoids false positives on a single sample).
   */
  function detectDelimiter(comboValues, fieldCount) {
    if (!comboValues.length) return DELIMITER_CANDIDATES[0];
    for (var i = 0; i < DELIMITER_CANDIDATES.length; i++) {
      var d = DELIMITER_CANDIDATES[i];
      if (delimiterValidForAll(comboValues, d, fieldCount)) return d;
    }
    return DELIMITER_CANDIDATES[0];
  }

  // ── Section Rendering API fetch ──────────────────────────────────────────────

  /**
   * Fetch the data section with the given option_value IDs.
   * Returns a Promise that resolves to the parsed JSON payload (or null on error).
   */
  function fetchSplitComboData(productUrl, optionValueIds) {
    var params = optionValueIds.length > 0
      ? '&option_values=' + optionValueIds.map(String).join(',')
      : '';
    var url = productUrl + '?section_id=' + DATA_SECTION_ID + params;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var dom = new DOMParser().parseFromString(html, 'text/html');
        var scriptEl = dom.querySelector('[data-stinner-split-combo-data] script');
        if (!scriptEl) return null;
        try { return JSON.parse(scriptEl.textContent); } catch (e) { return null; }
      })
      .catch(function (err) {
        console.warn(WARN_PREFIX, 'Data section fetch failed', err);
        return null;
      });
  }

  // ── Core init ────────────────────────────────────────────────────────────────

  function initFromConfigEl(cfgEl) {
    if (cfgEl.getAttribute('data-stinner-split-combo-initialized') === 'true') return;

    var section = cfgEl.closest('[data-section-type="product"]');
    if (!section) return;

    var cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent || '{}');
    } catch (e) {
      console.warn(WARN_PREFIX, 'Invalid config JSON', e);
      return;
    }

    var comboIdx = parseInt(cfg.comboOptionIndex, 10);
    if (isNaN(comboIdx) || comboIdx < 0 || comboIdx > 2) {
      console.warn(WARN_PREFIX, 'Invalid comboOptionIndex');
      return;
    }

    var comboKey = comboOptionKey(comboIdx);
    var others   = otherOptionKeys(comboIdx);

    var extraFields = cfg.extraFields;
    if (!Array.isArray(extraFields) || extraFields.length === 0) {
      extraFields = [
        { key: 'suspension', label: 'Suspension' },
        { key: 'wheel',      label: 'Wheelset'   },
      ];
    }

    // Record each field's position in the combo value string BEFORE any reorder.
    // partIndex keeps value parsing correct even when display order changes.
    extraFields.forEach(function (f, i) {
      if (typeof f.partIndex !== 'number') f.partIndex = i;
    });

    // Optional display-only reorder driven by cfg.displayOrder (labels or keys).
    if (Array.isArray(cfg.displayOrder) && cfg.displayOrder.length) {
      var orderedFields = [];
      cfg.displayOrder.forEach(function (name) {
        var match = extraFields.filter(function (f) {
          return norm(f.key) === norm(name) || norm(f.label) === norm(name);
        })[0];
        if (match && orderedFields.indexOf(match) === -1) orderedFields.push(match);
      });
      // Append any fields not named in displayOrder, preserving original order.
      extraFields.forEach(function (f) {
        if (orderedFields.indexOf(f) === -1) orderedFields.push(f);
      });
      if (orderedFields.length === extraFields.length) extraFields = orderedFields;
    }

    // Optional ordering of the option VALUES inside each selector (display only).
    var valueOrder = Array.isArray(cfg.valueOrder) ? cfg.valueOrder : [];

    // Combo values come from product_option_value objects — no variant cap.
    var comboValues = cfg.comboValues || [];
    if (!comboValues.length) {
      console.warn(WARN_PREFIX, 'No combo values found — native UI left as-is.');
      return;
    }

    // Resolve delimiter: use configured value or auto-detect.
    var delimiter = cfg.delimiter && cfg.delimiter !== 'auto'
      ? cfg.delimiter
      : detectDelimiter(comboValues, extraFields.length);

    function getBadVals(d) {
      return comboValues.filter(function (cv) {
        return splitComboValue(cv.value, d).length !== extraFields.length;
      });
    }

    var badVal = getBadVals(delimiter);
    // Fixed delimiter from Liquid can be wrong if product wording changed (e.g. + vs /).
    if (badVal.length && cfg.delimiter && cfg.delimiter !== 'auto') {
      console.warn(
        WARN_PREFIX,
        'Configured delimiter "' + cfg.delimiter + '" failed validation; falling back to auto-detect.'
      );
      delimiter = detectDelimiter(comboValues, extraFields.length);
      badVal = getBadVals(delimiter);
    }

    // Validate: every combo value should split into exactly extraFields.length parts.
    if (badVal.length) {
      console.warn(
        WARN_PREFIX,
        badVal.length + ' combo value(s) do not split into ' + extraFields.length +
        ' parts with delimiter "' + delimiter + '". First offender: "' + badVal[0].value +
        '". Native UI left as-is.'
      );
      return;
    }

    var nativeOptionGroups = cfg.nativeOptionGroups || [];
    var productUrl         = cfg.productUrl || window.location.pathname;
    var productId          = cfg.productId;

    var master = section.querySelector('[data-product-select]');
    if (!master) {
      console.warn(WARN_PREFIX, 'No master variant select');
      return;
    }

    var nativeWrap = findNativeComboWrapper(section, comboKey);
    if (!nativeWrap) {
      console.warn(WARN_PREFIX, 'Could not find native combo option wrapper');
      return;
    }

    var useCarrizoLabels = section.classList.contains('product-section--pdp-bike-series');

    // ── Build N selector rows ─────────────────────────────────────────────────
    var fieldsets   = [];
    var valueSpans  = [];
    var ui = document.createElement('div');
    ui.className = 'stinner-split-combo-custom';

    for (var fi = 0; fi < extraFields.length; fi++) {
      (function (fieldIndex) {
        var field = extraFields[fieldIndex];
        var fsId  = 'stinner-split-' + field.key + '-fs-' + productId;
        var row   = document.createElement('div');
        row.className = 'variant-wrapper js';
        row.setAttribute('data-type', 'button');

        var labelEl = document.createElement('label');
        labelEl.setAttribute('for', fsId);
        if (useCarrizoLabels) {
          labelEl.className = 'variant__label pdp-carrizo-variant-label';
          labelEl.innerHTML =
            '<span class="pdp-carrizo-opt-head">' + field.label.toUpperCase() + '</span>' +
            '<span class="variant__label-info pdp-carrizo-opt-val">' +
            '<span class="pdp-carrizo-opt-slash" aria-hidden="true">/</span>' +
            '<span data-stinner-split-label="' + field.key + '"></span>' +
            '</span>';
        } else {
          labelEl.className = 'variant__label';
          labelEl.innerHTML =
            field.label +
            '<span class="variant__label-info"> &mdash; <span data-stinner-split-label="' +
            field.key + '"></span></span>';
        }

        var fieldset = document.createElement('fieldset');
        fieldset.id  = fsId;
        fieldset.setAttribute('data-stinner-split-field', field.key);

        row.appendChild(labelEl);
        row.appendChild(fieldset);
        ui.appendChild(row);

        fieldsets[fieldIndex]  = fieldset;
        valueSpans[fieldIndex] = labelEl.querySelector('[data-stinner-split-label="' + field.key + '"]');
      })(fi);
    }

    nativeWrap.classList.add('stinner-split-combo-native-combo', 'is-stinner-split-combo-hidden');
    nativeWrap.insertAdjacentElement('afterend', ui);

    // Live combo values (updated on every data-section fetch).
    var liveComboValues = comboValues.slice();

    // ── Combo value helpers ───────────────────────────────────────────────────

    function subParts(cv) {
      return splitComboValue(cv.value, delimiter);
    }

    // Map a display row index to its position in the combo value string.
    function partIndexFor(displayIndex) {
      var f = extraFields[displayIndex];
      return f && typeof f.partIndex === 'number' ? f.partIndex : displayIndex;
    }

    // Reorder a list of option values per cfg.valueOrder (matched first, rest after).
    function applyValueOrder(values) {
      if (!valueOrder.length) return values;
      var ordered = [];
      valueOrder.forEach(function (name) {
        var match = values.filter(function (v) { return norm(v) === norm(name); })[0];
        if (match && ordered.indexOf(match) === -1) ordered.push(match);
      });
      values.forEach(function (v) { if (ordered.indexOf(v) === -1) ordered.push(v); });
      return ordered;
    }

    /** Filter combo values by sub-part selections at levels 0 … levelIndex-1. */
    function filterByLevelsAbove(pool, levelIndex) {
      for (var i = 0; i < levelIndex; i++) {
        var sel = norm(getRadioGroupValue(fieldsets[i]));
        var pidx = partIndexFor(i);
        pool = pool.filter(function (cv) { return norm(subParts(cv)[pidx]) === sel; });
      }
      return pool;
    }

    /** Find the single combo value matching ALL current sub-selector selections. */
    function resolveComboValue() {
      return liveComboValues.filter(function (cv) {
        var parts = subParts(cv);
        for (var i = 0; i < extraFields.length; i++) {
          if (norm(parts[partIndexFor(i)]) !== norm(getRadioGroupValue(fieldsets[i]))) return false;
        }
        return true;
      })[0] || null;
    }

    // ── Label spans ───────────────────────────────────────────────────────────

    function updateSplitComboLabels(overrides) {
      for (var i = 0; i < extraFields.length; i++) {
        var span = valueSpans[i];
        if (!span) continue;
        var text = overrides && overrides[i] != null && overrides[i] !== ''
          ? String(overrides[i])
          : getRadioGroupValue(fieldsets[i]);
        span.textContent = useCarrizoLabels ? text.toUpperCase() : text;
      }
    }

    // ── Refresh one button group ──────────────────────────────────────────────

    function refreshLevel(levelIndex, preferred) {
      var pool   = filterByLevelsAbove(liveComboValues, levelIndex);
      var pidx   = partIndexFor(levelIndex);
      var values = applyValueOrder(uniqueByNorm(pool.map(function (cv) { return subParts(cv)[pidx]; })));

      var soldOut = {};
      values.forEach(function (val) {
        var subset = pool.filter(function (cv) { return norm(subParts(cv)[pidx]) === norm(val); });
        if (!subset.some(function (cv) { return cv.available; })) soldOut[norm(val)] = true;
      });

      fillButtonGroup(fieldsets[levelIndex], values, preferred, {
        productId:    productId,
        radioName:    extraFields[levelIndex].key,
        legendText:   extraFields[levelIndex].label,
        soldOutValues: soldOut,
      });
    }

    // ── Build option_value ID array for the data-section fetch ────────────────

    function buildOptionValueIds(comboValueId) {
      var ids = [];

      // Collect option_value IDs for the native (non-combo) options.
      for (var i = 0; i < nativeOptionGroups.length; i++) {
        var grp        = nativeOptionGroups[i];
        var currentVal = norm(getOptionValue(section, grp.optionKey));
        var match      = grp.values.filter(function (v) { return norm(v.label) === currentVal; })[0];
        if (match) ids.push(String(match.id));
      }

      // Append the combo option_value ID.
      if (comboValueId != null) ids.push(String(comboValueId));

      return ids;
    }

    // ── Apply a resolved combo value + variant ────────────────────────────────

    /**
     * Dispatch variantChange so Impulse updates price, buy-button state, etc.
     * We pass the partial variant object we received from the data section;
     * the theme's handler uses at minimum variant.id and variant.available.
     */
    function dispatchVariantChange(variantData, lastChange) {
      if (!variantData || !variantData.id) {
        dispatchUnavailable(section);
        return;
      }
      var lc = lastChange || {};
      section.dispatchEvent(new CustomEvent('variantChange', {
        detail: {
          variant:                 variantData,
          currentlySelectedValues: buildCurrentlySelectedValues(section),
          value:                   lc.value != null ? lc.value : null,
          index:                   lc.index != null ? lc.index : null,
        },
        bubbles: true,
      }));
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant: variantData },
      }));
    }

    /**
     * After JS resolves a combo value:
     *  1. Set the native hidden combo input so the theme form has the right value.
     *  2. Fetch the data section with all three option_value IDs.
     *  3. Apply the refreshed availability states + sync master select.
     */
    function applyComboValue(comboVal) {
      if (!comboVal) {
        dispatchUnavailable(section);
        return;
      }

      // Keep the native combo input in sync (used by Impulse's own variant handlers).
      var nativeTrigger = setOptionValue(section, comboKey, comboVal.value);
      var nativeEl = nativeTrigger ||
        section.querySelector('[data-variant-input][data-index="' + comboKey + '"]');
      if (nativeEl) dispatchVariantInputChange(nativeEl);

      // Update label spans immediately (before fetch).
      updateSplitComboLabels(null);

      // Refresh availability via Section Rendering API.
      var ids = buildOptionValueIds(comboVal.id);
      fetchSplitComboData(productUrl, ids).then(function (data) {
        if (!data || !data.active) {
          dispatchUnavailable(section);
          return;
        }

        // Update live combo values with fresh availability from Shopify.
        liveComboValues = data.comboValues || liveComboValues;

        // Re-render all button groups with updated sold-out states.
        for (var i = 0; i < extraFields.length; i++) {
          refreshLevel(i, getRadioGroupValue(fieldsets[i]));
        }
        updateSplitComboLabels(null);

        // Sync master <select> (creates <option> if variant is 251+).
        if (data.selectedVariantId) {
          syncMasterVariantSelect(section, data.selectedVariantId);
        }

        var comboWrap = nativeEl && nativeEl.closest('.variant-input');
        var lastChange = nativeEl
          ? {
              value: nativeEl.value,
              index:
                (comboWrap && comboWrap.getAttribute('data-index')) ||
                nativeEl.getAttribute('data-index'),
            }
          : null;

        // Dispatch variantChange with full data for price / button updates.
        dispatchVariantChange(
          {
            id:               data.selectedVariantId,
            available:        data.selectedVariantAvailable,
            price:            data.selectedVariantPrice,
            compare_at_price: data.selectedVariantCompareAtPrice,
            title:            data.selectedVariantTitle,
          },
          lastChange
        );
      });
    }

    // ── Sub-selector change handler ───────────────────────────────────────────

    function onSubSelectorChange(changedLevelIndex) {
      // Cascade: refresh all levels below the changed one.
      for (var j = changedLevelIndex + 1; j < fieldsets.length; j++) {
        refreshLevel(j, null);
      }
      updateSplitComboLabels(null);

      var comboVal = resolveComboValue();
      applyComboValue(comboVal);
    }

    fieldsets.forEach(function (fs, i) {
      fs.addEventListener('change', function () { onSubSelectorChange(i); });
    });

    // ── Native option (Color / Size) change handler ───────────────────────────

    function onNativeOptionChange() {
      // Refresh all sub-selector levels from scratch.
      for (var j = 0; j < fieldsets.length; j++) {
        refreshLevel(j, null);
      }
      updateSplitComboLabels(null);

      var comboVal = resolveComboValue();
      applyComboValue(comboVal);
    }

    others.forEach(function (optKey) {
      var nativeInputs = section.querySelectorAll(
        '[data-variant-input][data-index="' + optKey + '"]'
      );
      nativeInputs.forEach(function (inp) {
        inp.addEventListener('change', onNativeOptionChange);
      });
    });

    // ── Initial render ────────────────────────────────────────────────────────

    // Find the currently-selected combo value to seed each level.
    var initialComboVal = liveComboValues.filter(function (cv) { return cv.selected; })[0]
      || liveComboValues[0];

    var initialSubParts = initialComboVal ? subParts(initialComboVal) : [];

    for (var li = 0; li < extraFields.length; li++) {
      refreshLevel(li, initialSubParts[partIndexFor(li)] || null);
    }
    var initialDisplay = [];
    for (var di = 0; di < extraFields.length; di++) {
      initialDisplay[di] = initialSubParts[partIndexFor(di)];
    }
    updateSplitComboLabels(initialDisplay.length ? initialDisplay : null);

    // Sync master select to the initial selection without a fetch
    // (page load already has the correct variant in the form).
    if (cfg.selectedVariantId) {
      ensureMasterSelectOption(master, cfg.selectedVariantId);
      var curVal = String(cfg.selectedVariantId);
      if (master.value !== curVal) {
        master.value = curVal;
      }
    }

    cfgEl.setAttribute('data-stinner-split-combo-initialized', 'true');
  }

  // ── Boot ─────────────────────────────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('[data-stinner-split-combo-config]').forEach(initFromConfigEl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 50); });
  } else {
    setTimeout(boot, 50);
  }
  document.addEventListener('page:loaded', boot);
})();
